import { useCallback, useEffect, useRef, useState } from "react";
import {
  Bot,
  ChevronDown,
  Copy,
  Maximize2,
  Minimize2,
  Send,
  Share2,
  Wrench,
  X,
} from "lucide-react";
import {
  OFFICE_DOCUMENT_TEXT_MAX,
  sendOfficeAssistantMessage,
  type AssistantChatMessage,
  type OfficeAssistantMode,
  type OfficeEdit,
} from "@nevisign/reader-core";
import {
  getOrCreateChatForFile,
  listChatThreads,
  updateChatFileMeta,
  updateChatMessages,
  type FileChatThread,
  type ReaderChatMessage,
} from "../lib/chatStore";
import { applyOfficeEditsToDom } from "../lib/applyOfficeEdits";
import { appendLog } from "../lib/logger";
import { useKeyboardInset } from "../hooks/useKeyboardInset";
import { copyText, shareText } from "../lib/shareText";
import type { ReaderTokenUsage } from "../lib/readerTools";
import { fetchCloudChat, pushCloudChat } from "../lib/cloudChat";
import { handleApiAuthError } from "../lib/auth-required";

type Props = {
  open: boolean;
  fullscreen: boolean;
  apiBase: string;
  documentTitle?: string;
  documentBytes?: number;
  documentText?: string;
  /** When set, chat history syncs with studio for this Nevisign document. */
  nevisignDocumentId?: string | null;
  scopedSelection?: string | null;
  pendingQuote?: string | null;
  onPendingQuoteConsumed?: () => void;
  onClearScopedSelection?: () => void;
  editRootRef?: React.RefObject<HTMLElement | null>;
  activeChatId?: string | null;
  onActiveChatIdChange?: (id: string) => void;
  fileMissingHint?: string | null;
  onClose: () => void;
  onToggleFullscreen: () => void;
  onOpenTools?: () => void;
};

const MODES: { id: OfficeAssistantMode; label: string }[] = [
  { id: "auto", label: "خودکار" },
  { id: "legal", label: "حقوقی تخصصی" },
  { id: "general", label: "دستیار غیرتخصصی" },
];

function asReaderMsg(m: AssistantChatMessage): ReaderChatMessage {
  return m as ReaderChatMessage;
}

export function AssistantSheet({
  open,
  fullscreen,
  apiBase,
  documentTitle,
  documentBytes,
  documentText,
  nevisignDocumentId,
  scopedSelection,
  pendingQuote,
  onPendingQuoteConsumed,
  onClearScopedSelection,
  editRootRef,
  activeChatId,
  onActiveChatIdChange,
  fileMissingHint,
  onClose,
  onToggleFullscreen,
  onOpenTools,
}: Props) {
  const [thread, setThread] = useState<FileChatThread | null>(null);
  const [input, setInput] = useState("");
  const [quoteChip, setQuoteChip] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [hint, setHint] = useState<string | null>(null);
  const [mode, setMode] = useState<OfficeAssistantMode>("auto");
  const [modeOpen, setModeOpen] = useState(false);
  const [lastEdits, setLastEdits] = useState<OfficeEdit[]>([]);
  const [usageOpenIdx, setUsageOpenIdx] = useState<number | null>(null);
  const [sessionUsage, setSessionUsage] = useState<ReaderTokenUsage | null>(
    null
  );
  const [sessionUsageOpen, setSessionUsageOpen] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const keyboardInset = useKeyboardInset();

  useEffect(() => {
    if (!documentTitle) {
      setThread(null);
      return;
    }
    if (activeChatId) {
      const fromList = listChatThreads().find((t) => t.id === activeChatId);
      if (fromList) {
        setThread(fromList);
        return;
      }
    }
    const created = getOrCreateChatForFile(documentTitle, documentBytes, {
      nevisignDocumentId: nevisignDocumentId || undefined,
    });
    setThread(created);
    onActiveChatIdChange?.(created.id);
  }, [
    documentTitle,
    documentBytes,
    activeChatId,
    onActiveChatIdChange,
    nevisignDocumentId,
  ]);

  // Unified history with Nevisign studio when document is linked
  useEffect(() => {
    if (!nevisignDocumentId || !thread || !apiBase) return;
    let cancelled = false;
    void (async () => {
      const cloud = await fetchCloudChat(apiBase, nevisignDocumentId);
      if (cancelled || !cloud || cloud.length === 0) return;
      // Prefer cloud when it has real dialogue (more than welcome alone)
      const cloudHasDialogue = cloud.some((m) => m.role === "user");
      const localHasDialogue = thread.messages.some((m) => m.role === "user");
      if (cloudHasDialogue && (!localHasDialogue || cloud.length >= thread.messages.length)) {
        updateChatMessages(thread.id, cloud);
        updateChatFileMeta(thread.id, { nevisignDocumentId });
        setThread((prev) =>
          prev ? { ...prev, messages: cloud, nevisignDocumentId } : prev
        );
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nevisignDocumentId, apiBase, thread?.id]);

  useEffect(() => {
    if (!pendingQuote) return;
    const q = pendingQuote.trim();
    if (q) setQuoteChip(q);
    setInput("");
    onPendingQuoteConsumed?.();
  }, [pendingQuote, onPendingQuoteConsumed]);

  useEffect(() => {
    if (scopedSelection?.trim()) {
      setQuoteChip(scopedSelection.trim());
    }
  }, [scopedSelection]);

  const messages = (thread?.messages ?? []) as ReaderChatMessage[];

  useEffect(() => {
    if (!open) return;
    const el = listRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    });
  }, [messages, open, busy, hint]);

  const selectionForApply = (quoteChip || scopedSelection || "").trim();
  const scopeToSelection = Boolean(selectionForApply);

  const applyEdits = useCallback(
    (edits: OfficeEdit[]) => {
      if (!edits.length) return;
      const result = applyOfficeEditsToDom(
        editRootRef?.current ?? null,
        edits,
        selectionForApply || undefined,
        { scopeToSelection }
      );
      setHint(
        result.applied
          ? `${result.applied.toLocaleString("fa-IR")} ویرایش اعمال شد` +
              (result.occurrenceCount > result.applied
                ? ` (${result.occurrenceCount.toLocaleString("fa-IR")} مورد)`
                : "") +
              (result.failed
                ? ` (${result.failed.toLocaleString("fa-IR")} ناموفق)`
                : "") +
              (scopeToSelection ? " — فقط روی متن انتخاب‌شده." : " — در کل سند.")
          : "ویرایشی روی سند اعمال نشد (متن پیدا نشد)."
      );
    },
    [editRootRef, scopeToSelection, selectionForApply]
  );

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || busy || !thread) return;
    setInput("");
    setBusy(true);
    setHint(null);
    setLastEdits([]);
    setModeOpen(false);

    const chip = (quoteChip || "").trim();
    const userContent = chip ? `${text}\n\n«${chip}»` : text;
    const userMsg: ReaderChatMessage = { role: "user", content: userContent };
    const recent = [...thread.messages, userMsg].slice(-8);
    const withUser = [...thread.messages, userMsg] as ReaderChatMessage[];
    setThread({ ...thread, messages: withUser });
    updateChatMessages(thread.id, withUser);

    const docText = (documentText ?? "").trim();
    const truncated =
      docText.length > OFFICE_DOCUMENT_TEXT_MAX
        ? docText.slice(0, OFFICE_DOCUMENT_TEXT_MAX)
        : docText;
    const sel = chip;

    appendLog("info", "assistant", "office request", {
      mode,
      docChars: truncated.length,
      selectionChars: sel.length,
      messageChars: text.length,
      scoped: Boolean(sel),
    });

    try {
      const { buildSectionIndexFromText } = await import("@nevisign/edit-ops");
      const sectionIndex = truncated
        ? buildSectionIndexFromText(truncated)
        : undefined;
      const result = await sendOfficeAssistantMessage(apiBase, {
        message: text,
        document_text: truncated || undefined,
        selection: sel ? { text: sel } : null,
        context: sel ? [{ kind: "selection", quote: sel }] : undefined,
        mode,
        recent: recent.slice(0, -1).map(asReaderMsg),
        section_index: sectionIndex,
        document_fingerprint: sectionIndex?.document_fingerprint,
        document_id: nevisignDocumentId || undefined,
      });

      const usage = (result as { usage?: ReaderTokenUsage }).usage;
      if (usage?.nevisign_tokens) {
        setSessionUsage((prev) => mergeUsage(prev, usage));
      }

      appendLog("info", "assistant", "office reply", {
        replyChars: result.reply.length,
        edits: result.edits.length,
        resolved: result.assistant?.resolved,
        tokens: usage?.nevisign_tokens,
      });

      let reply = result.reply;
      if (result.edits.length) {
        reply += `\n\n— ${result.edits.length.toLocaleString("fa-IR")} پیشنهاد ویرایش آماده است.`;
        setLastEdits(result.edits);
      }

      const withReply: ReaderChatMessage[] = [
        ...withUser,
        { role: "assistant", content: reply, usage },
      ];
      setThread((prev) =>
        prev
          ? { ...prev, messages: withReply, updatedAt: new Date().toISOString() }
          : prev
      );
      updateChatMessages(thread.id, withReply);
      if (nevisignDocumentId) {
        void pushCloudChat(apiBase, nevisignDocumentId, withReply);
      }

      if (result.edits.length && editRootRef?.current) {
        applyEdits(result.edits);
      }
    } catch (err) {
      if (handleApiAuthError(err, () => void send())) {
        setThread((prev) => {
          if (!prev) return prev;
          const msgs = prev.messages;
          if (msgs.at(-1)?.role === "user") {
            const reverted = msgs.slice(0, -1);
            updateChatMessages(prev.id, reverted);
            return { ...prev, messages: reverted };
          }
          return prev;
        });
        setInput(text);
        setBusy(false);
        return;
      }
      const msg =
        err instanceof Error ? err.message : "خطا در ارتباط با دستیار";
      appendLog("error", "assistant", msg);
      setHint(msg);
      const withErr: ReaderChatMessage[] = [
        ...withUser,
        {
          role: "assistant",
          content: `متأسفم — ارتباط با سرور نویساین برقرار نشد:\n${msg}`,
        },
      ];
      setThread((prev) => (prev ? { ...prev, messages: withErr } : prev));
      updateChatMessages(thread.id, withErr);
      if (nevisignDocumentId) {
        void pushCloudChat(apiBase, nevisignDocumentId, withErr);
      }
    } finally {
      setBusy(false);
    }
  }, [
    apiBase,
    applyEdits,
    busy,
    documentText,
    editRootRef,
    input,
    mode,
    nevisignDocumentId,
    quoteChip,
    thread,
  ]);

  if (!open) return null;

  const docChars = (documentText ?? "").trim().length;
  const modeLabel = MODES.find((m) => m.id === mode)?.label ?? "خودکار";
  const bottomOffset = keyboardInset > 0 ? keyboardInset : 0;

  return (
    <div
      className={`assistant-sheet${fullscreen ? " fullscreen" : ""}`}
      role="dialog"
      aria-label="دستیار نویساین"
      style={{
        bottom: bottomOffset,
        paddingBottom:
          bottomOffset > 0 ? "0.35rem" : "var(--safe-bottom)",
        maxHeight:
          bottomOffset > 0
            ? `calc(100dvh - ${bottomOffset}px)`
            : undefined,
      }}
    >
      <div className="assistant-handle" aria-hidden />
      <div className="assistant-header">
        <div className="assistant-title">
          <Bot size={17} />
          <span>دستیار نویساین</span>
        </div>
        <div className="assistant-header-actions">
          {sessionUsage && sessionUsage.nevisign_tokens > 0 && (
            <button
              type="button"
              className="assistant-token-chip"
              onClick={() => setSessionUsageOpen((o) => !o)}
            >
              {sessionUsage.nevisign_tokens.toLocaleString("fa-IR")} توکن
            </button>
          )}
          {onOpenTools && (
            <button
              type="button"
              className="icon-btn compact"
              aria-label="ابزارها"
              title="ابزارها"
              onClick={onOpenTools}
            >
              <Wrench size={15} />
              <span style={{ fontSize: "0.72rem", marginInlineStart: "0.2rem" }}>
                ابزارها
              </span>
            </button>
          )}
          <button
            type="button"
            className="icon-btn"
            aria-label={fullscreen ? "کوچک کردن" : "تمام‌صفحه"}
            onClick={onToggleFullscreen}
          >
            {fullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
          </button>
          <button
            type="button"
            className="icon-btn"
            aria-label="بستن"
            onClick={() => {
              setQuoteChip(null);
              onClearScopedSelection?.();
              onClose();
            }}
          >
            <X size={16} />
          </button>
        </div>
      </div>
      {sessionUsageOpen && sessionUsage && (
        <div className="assistant-token-panel" style={{ margin: "0 0.75rem" }}>
          <UsageBreakdown usage={sessionUsage} />
        </div>
      )}
      {documentTitle && (
        <div className="assistant-file-hint" title={documentTitle}>
          گفتگو برای: {documentTitle}
          {docChars > 0
            ? ` · ${docChars.toLocaleString("fa-IR")} نویسه سند پیوست می‌شود`
            : " · هشدار: متن سند خالی است"}
        </div>
      )}
      {fileMissingHint && (
        <div className="assistant-file-missing" role="status">
          {fileMissingHint}
        </div>
      )}

      <div className="assistant-messages" ref={listRef}>
        {messages.length === 0 && !busy ? (
          <div className="assistant-empty-state">
            <div className="hero-art" style={{ maxWidth: "10rem", marginBottom: "0.75rem" }}>
              <img src="/illustrations/assistant-hero.svg" alt="تصویر دستیار نویساین" />
            </div>
            <strong>از همین‌جا شروع کنید</strong>
            <p className="settings-hint">
              سؤال حقوقی بپرسید، خلاصه بخواهید یا اگر روی متن انتخابی کار می‌کنید آن را به دستیار بسپارید.
            </p>
          </div>
        ) : null}
        {messages.map((m, i) => (
          <div
            key={`${m.role}-${i}`}
            className={`assistant-bubble-wrap ${m.role}`}
          >
            <div className={`assistant-bubble ${m.role}`}>{m.content}</div>
            <div className="assistant-bubble-actions">
              <button
                type="button"
                className="icon-btn compact"
                aria-label="کپی"
                onClick={() => {
                  void copyText(m.content)
                    .then(() => setHint("پیام کپی شد"))
                    .catch(() => setHint("کپی ناموفق"));
                }}
              >
                <Copy size={13} />
              </button>
              <button
                type="button"
                className="icon-btn compact"
                aria-label="اشتراک"
                onClick={() => {
                  void shareText(m.content, `nevisign-chat-${i + 1}.txt`)
                    .then(() => setHint("اشتراک باز شد"))
                    .catch((e) =>
                      setHint(e instanceof Error ? e.message : "اشتراک ناموفق")
                    );
                }}
              >
                <Share2 size={13} />
              </button>
            </div>
            {m.usage && m.usage.nevisign_tokens > 0 && (
              <>
                <button
                  type="button"
                  className="assistant-token-chip"
                  onClick={() =>
                    setUsageOpenIdx((cur) => (cur === i ? null : i))
                  }
                >
                  {m.usage.nevisign_tokens.toLocaleString("fa-IR")} توکن نویساین
                </button>
                {usageOpenIdx === i && (
                  <div className="assistant-token-panel">
                    <UsageBreakdown usage={m.usage} />
                  </div>
                )}
              </>
            )}
          </div>
        ))}
        {busy && (
          <div className="assistant-bubble assistant thinking">در حال فکر…</div>
        )}
        {hint && <p className="settings-hint">{hint}</p>}
        {lastEdits.length > 0 && (
          <button
            type="button"
            className="icon-btn compact"
            onClick={() => applyEdits(lastEdits)}
          >
            اعمال مجدد ویرایش‌ها ({lastEdits.length.toLocaleString("fa-IR")})
          </button>
        )}
      </div>

      <div className="assistant-compose-wrap">
        {quoteChip && (
          <div className="assistant-quote-chip" title={quoteChip}>
            <span className="assistant-quote-chip-text">
              {quoteChip.length > 140
                ? `${quoteChip.slice(0, 140)}…`
                : quoteChip}
            </span>
            <button
              type="button"
              className="icon-btn compact"
              aria-label="حذف نقل‌قول"
              onClick={() => {
                setQuoteChip(null);
                onClearScopedSelection?.();
              }}
            >
              <X size={14} />
            </button>
          </div>
        )}
        <div className="assistant-compose">
          <div className="assistant-compose-main">
            <textarea
              rows={3}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="پیام خود را بنویسید…"
              disabled={busy}
              autoComplete="off"
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  void send();
                }
              }}
            />
          </div>
          <div className="assistant-compose-toolbar">
            <button
              type="button"
              className="icon-btn primary assistant-send"
              disabled={busy || !input.trim()}
              aria-label="ارسال"
              onClick={() => void send()}
            >
              <Send size={16} />
            </button>
            <div className="assistant-mode-select">
              <button
                type="button"
                className="assistant-mode-trigger"
                aria-haspopup="listbox"
                aria-expanded={modeOpen}
                onClick={() => setModeOpen((o) => !o)}
              >
                {modeLabel}
                <ChevronDown size={14} />
              </button>
              {modeOpen && (
                <ul className="assistant-mode-menu" role="listbox">
                  {MODES.map((m) => (
                    <li key={m.id}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={mode === m.id}
                        className={mode === m.id ? "active" : undefined}
                        onClick={() => {
                          setMode(m.id);
                          setModeOpen(false);
                        }}
                      >
                        {m.label}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function UsageBreakdown({ usage }: { usage: ReaderTokenUsage }) {
  const gemma =
    usage.by_provider.gemma && usage.by_provider.gemma.total_tokens > 0
      ? usage.by_provider.gemma
      : null;
  const dadno =
    usage.by_provider.dadno && usage.by_provider.dadno.total_tokens > 0
      ? usage.by_provider.dadno
      : null;
  return (
    <>
      {gemma && (
        <p>
          دستیار غیرتخصصی — ورودی {fa(gemma.prompt_tokens)} · خروجی{" "}
          {fa(gemma.completion_tokens)} · جمع {fa(gemma.total_tokens)}
        </p>
      )}
      {dadno && (
        <p>
          دادنو — ورودی {fa(dadno.prompt_tokens)} · خروجی{" "}
          {fa(dadno.completion_tokens)} · جمع {fa(dadno.total_tokens)}
        </p>
      )}
      {usage.profile && (
        <p>
          دستیار:{" "}
          {usage.profile === "legal" ? "حقوقی تخصصی" : "دستیار غیرتخصصی"}
        </p>
      )}
    </>
  );
}

function fa(n: number) {
  return n.toLocaleString("fa-IR");
}

function mergeUsage(
  prev: ReaderTokenUsage | null,
  next: ReaderTokenUsage
): ReaderTokenUsage {
  if (!prev) return next;
  const sum = (
    a?: { prompt_tokens: number; completion_tokens: number; total_tokens: number },
    b?: { prompt_tokens: number; completion_tokens: number; total_tokens: number }
  ) => {
    if (!a && !b) return undefined;
    return {
      prompt_tokens: (a?.prompt_tokens ?? 0) + (b?.prompt_tokens ?? 0),
      completion_tokens:
        (a?.completion_tokens ?? 0) + (b?.completion_tokens ?? 0),
      total_tokens: (a?.total_tokens ?? 0) + (b?.total_tokens ?? 0),
    };
  };
  return {
    nevisign_tokens: prev.nevisign_tokens + next.nevisign_tokens,
    by_provider: {
      gemma: sum(prev.by_provider.gemma, next.by_provider.gemma),
      dadno: sum(prev.by_provider.dadno, next.by_provider.dadno),
    },
    profile: next.profile ?? prev.profile,
  };
}

type FabProps = {
  onClick: () => void;
};

export function AssistantFab({ onClick }: FabProps) {
  return (
    <button
      type="button"
      className="assistant-fab"
      aria-label="دستیار هوش مصنوعی"
      onClick={onClick}
    >
      <Bot size={22} />
    </button>
  );
}
